import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../services/api'; // ← Importar API_BASE
import {
    Home,
    PawPrint,
    QrCode,
    Calendar,
    Settings,
    LogOut,
    User,
    ChevronDown,
    Menu,
    X,
    HeartHandshake,
} from 'lucide-react';
import './UserHeader.css';

export function UserHeader() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const dropdownRef = useRef(null);

    const [mobileOpen, setMobileOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [photoError, setPhotoError] = useState(false);

    const userId = user?.id;

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setDropdownOpen(false);
        setPhotoError(false);
    }, [location.pathname]);

    // Cerrar dropdown al click fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // ═══════════════════════════════════════════════════════════
    // NAV ITEMS — todas las rutas bajo /consultas/:id/...
    // ═══════════════════════════════════════════════════════════
    const navItems = [
        { to: `/consultas/${userId}`, label: 'Inicio', icon: Home },
        { to: `/consultas/${userId}/mismascotas`, label: 'Mis mascotas', icon: PawPrint },
        { to: `/consultas/${userId}/agenda`, label: 'Agenda', icon: Calendar },
        { to: `/consultas/${userId}/socios`, label: 'Socios', icon: HeartHandshake },
    ];

    const dropdownItems = [
        { to: `/consultas/${userId}`, label: 'Inicio', icon: Home },
        { to: `/consultas/${userId}/mismascotas`, label: 'Mis mascotas', icon: PawPrint },
        { to: `/consultas/${userId}/agenda`, label: 'Agenda', icon: Calendar },
        { to: `/consultas/${userId}/socios`, label: 'Socios', icon: HeartHandshake },
        { to: `/consultas/${userId}/configuracion`, label: 'Configuración', icon: Settings },
    ];

    // ═══════════════════════════════════════════════════════════
    // ACTIVE STATE
    // ═══════════════════════════════════════════════════════════
    const isActive = (path) => {
        const current = location.pathname;
        if (path === `/consultas/${userId}`) {
            return current === `/consultas/${userId}` ||
                current === `/Consultas/${userId}` ||
                current === `/consultas/${userId}/configuracion` ||
                current === `/Consultas/${userId}/configuracion`;
        }
        return current.toLowerCase() === path.toLowerCase();
    };

    // ═══════════════════════════════════════════════════════════
    // PHOTO URL usando API_BASE
    // ═══════════════════════════════════════════════════════════
    const getPhotoUrl = () => {
        if (!user?.foto_perfil) return null;
        if (user.foto_perfil.startsWith('http://') || user.foto_perfil.startsWith('https://')) {
            return user.foto_perfil;
        }
        const cleanBase = API_BASE.replace(/\/$/, '');
        const cleanPath = user.foto_perfil.replace(/^\//, '');
        return `${cleanBase}/${cleanPath}`;
    };

    const photoUrl = getPhotoUrl();

    // ═══════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════
    return (
        <header className={`sp-uheader ${scrolled ? 'sp-uheader--scrolled' : ''}`}>
            <div className="sp-uheader__inner">
                {/* Logo */}
                <Link className="sp-uheader__logo" to={`/consultas/${userId}`} aria-label="SmartPet">
                    <span className="sp-uheader__brand">Smart<span>Pet</span></span>
                </Link>

                {/* Desktop Nav */}
                <nav className="sp-uheader__nav">
                    {navItems.map((item) => (
                        <Link
                            key={item.label}
                            to={item.to}
                            className={`sp-uheader__link ${isActive(item.to) ? 'sp-uheader__link--active' : ''}`}
                        >
                            <item.icon size={15} />
                            {item.label}
                        </Link>
                    ))}
                </nav>

                {/* Right: User Dropdown */}
                <div className="sp-uheader__right" ref={dropdownRef}>
                    <button
                        className="sp-uheader__user"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                        aria-expanded={dropdownOpen}
                        aria-label="Menú de usuario"
                    >
                        <div className="sp-uheader__avatar">
                            {photoUrl && !photoError ? (
                                <img
                                    src={photoUrl}
                                    alt=""
                                    onError={() => {
                                        console.log('[UserHeader] Photo failed to load:', photoUrl);
                                        setPhotoError(true);
                                    }}
                                />
                            ) : (
                                <User size={18} />
                            )}
                        </div>
                        <span className="sp-uheader__name">{user?.nombre || 'Usuario'}</span>
                        <ChevronDown size={14} className={`sp-uheader__chevron ${dropdownOpen ? 'sp-uheader__chevron--open' : ''}`} />
                    </button>

                    {/* Dropdown */}
                    <div className={`sp-uheader__dropdown ${dropdownOpen ? 'sp-uheader__dropdown--open' : ''}`}>
                        {dropdownItems.map((item) => (
                            <button
                                key={item.label}
                                className="sp-uheader__drop-item"
                                onClick={() => {
                                    navigate(item.to);
                                    setDropdownOpen(false);
                                }}
                            >
                                <item.icon size={16} /> {item.label}
                            </button>
                        ))}
                        <div className="sp-uheader__drop-divider" />
                        <button className="sp-uheader__drop-item sp-uheader__drop-item--danger" onClick={handleLogout}>
                            <LogOut size={16} /> Cerrar sesión
                        </button>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        className="sp-uheader__hamburger"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
                    >
                        {mobileOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            <div className={`sp-uheader__mobile ${mobileOpen ? 'sp-uheader__mobile--open' : ''}`}>
                {/* User info en mobile */}
                <div className="sp-uheader__mobile-user">
                    <div className="sp-uheader__mobile-avatar">
                        {photoUrl && !photoError ? (
                            <img
                                src={photoUrl}
                                alt=""
                                onError={() => setPhotoError(true)}
                            />
                        ) : (
                            <User size={24} />
                        )}
                    </div>
                    <div>
                        <p className="sp-uheader__mobile-name">{user?.nombre} {user?.apellido}</p>
                        <p className="sp-uheader__mobile-email">{user?.email}</p>
                    </div>
                </div>

                <div className="sp-uheader__mobile-divider" />

                {/* Mobile nav links */}
                {dropdownItems.map((item) => (
                    <Link
                        key={item.label}
                        to={item.to}
                        className="sp-uheader__mobile-link"
                        onClick={() => setMobileOpen(false)}
                    >
                        <item.icon size={18} /> {item.label}
                    </Link>
                ))}

                <div className="sp-uheader__mobile-divider" />

                <button className="sp-uheader__mobile-link sp-uheader__mobile-link--danger" onClick={handleLogout}>
                    <LogOut size={18} /> Cerrar sesión
                </button>
            </div>
        </header>
    );
}